import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type ChangeEvent } from "react";
import { fetchPublicJobs, companyName, type Job } from "@/components/site/jobs";
import { PasswordInput } from "@/components/site/PasswordInput";
import { FindMoreJobs } from "@/components/site/FindMoreJobs";
import { CandidateShell } from "@/components/site/CandidateShell";

import { emptyProfile, type CandidateProfile } from "@/components/site/candidate";
import { assessmentDone, emptyShowcase, profileUrl, slugFor, type ShowcaseProfile } from "@/components/site/showcase";
import {
  fetchMyApplications,
  type MyApplication,
  fetchMyProfile,
  fetchMyShowcase,
  fetchSavedJobs,
  getCandidateSession,
  getCandidateToken,
  unsaveJob,
  updateMyProfile,
  updatePasswordApi,
  type SavedJob,
} from "@/lib/candidate-api";



export const Route = createFileRoute("/dashboard")({
  head: () => {
    const t = "Your candidate dashboard | CLOSDD";
    const d =
      "Track every sales role you've applied for and manage your CLOSDD profile in one place.";
    return {
      meta: [
        { name: "robots", content: "noindex,nofollow" },
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  validateSearch: (search: Record<string, unknown>): { tab?: "account" | "autoapply" } =>
    search["tab"] === "account" || search["tab"] === "autoapply" ? { tab: search["tab"] } : {},
  component: Dashboard,
});

type Tab = "overview" | "account" | "autoapply";

const inputClass =
  "w-full min-h-[46px] rounded-[4px] border border-[#D6D3C8] bg-white px-3 text-[15px] text-[#0B2160] outline-none focus:border-[#0B2160] focus:ring-2 focus:ring-[#FF5A3C]/40";

/** Mirrors the real dashboard's sidebar + main-column layout so nothing jumps once data arrives. */
function DashboardSkeleton() {
  const bar = "animate-pulse rounded-[4px] bg-[#E3E1D9]";
  return (
    <div className="cl min-h-screen bg-[#F4F3EE]" aria-busy="true" aria-label="Loading">
      <header className="bg-[#0B2160]">
        <div className="mx-auto flex w-full max-w-[1120px] flex-wrap items-center justify-between gap-3 px-6 py-3.5">
          <div className="flex items-center gap-3.5">
            <span className="flex items-center gap-2 text-[22px] font-extrabold tracking-[-0.03em] text-white">
              <span aria-hidden className="inline-block h-[9px] w-[9px] rounded-full bg-[#FF5A3C]" />
              closdd
            </span>
            <span className="border-l border-[#2A4393] pl-3.5 font-mono text-[11px] uppercase tracking-[0.16em] text-[#B9C6EE]">
              Candidate
            </span>
          </div>
          <div className="h-11 w-28 animate-pulse rounded-[4px] bg-white/15" />
        </div>
      </header>
      <div className="border-b border-[#E3E1D9] bg-white">
        <div className="mx-auto flex w-full max-w-[1120px] gap-2 px-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="px-3 pb-[13px] pt-4">
              <div className={`h-5 w-20 ${bar}`} />
            </div>
          ))}
        </div>
      </div>
      <main className="mx-auto grid w-full max-w-[860px] gap-4 px-6 pb-16 pt-8">
        <div className={`h-9 w-64 ${bar}`} />
        <div className="h-48 animate-pulse rounded-lg border border-[#E3E1D9] bg-white" />
        <div className="h-32 animate-pulse rounded-lg border border-[#E3E1D9] bg-white" />
        <div className="h-32 animate-pulse rounded-lg border border-[#E3E1D9] bg-white" />
      </main>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const tab: Tab = Route.useSearch().tab ?? "overview";
  const [profile, setProfile] = useState<CandidateProfile>(emptyProfile);
  const [showcase, setShowcase] = useState<ShowcaseProfile>(emptyShowcase);
  const [initialTab] = useState(tab);

  useEffect(() => {
    let cancelled = false;
    if (!getCandidateToken()) {
      navigate({ to: "/login" });
      return;
    }
    // Everything goes out alongside the session check instead of after it —
    // waiting on /auth/me first, then the profile, then each card's own
    // fetch was three round trips back to back behind the skeleton.
    const data = Promise.all([fetchMyProfile(), fetchMyShowcase()]);
    data.catch(() => undefined);
    if (initialTab === "overview") prefetchOverviewCards();
    getCandidateSession().then((user) => {
      if (cancelled) return;
      if (!user) {
        overviewPrefetch = {};
        navigate({ to: "/login" });
        return;
      }
      data
        .then(([p, s]) => {
          if (cancelled) return;
          setProfile(p);
          setShowcase(s);
          setReady(true);
          setLoadError("");
        })
        .catch((err) => {
          // The session is already confirmed valid above — a failed
          // profile fetch here is transient, not a logged-out state.
          if (!cancelled) {
            setLoadError(err instanceof Error ? err.message : "We couldn't load your dashboard. Try again.");
          }
        });
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, retryCount, initialTab]);

  if (loadError) {
    return (
      <div className="cl grid min-h-screen place-items-center bg-[#F4F3EE] px-6">
        <div className="grid max-w-[420px] gap-4 text-center">
          <p className="text-[17px] text-[#3A3A40]">{loadError}</p>
          <button
            type="button"
            onClick={() => setRetryCount((n) => n + 1)}
            className="mx-auto min-h-11 rounded-[4px] bg-[#FF5A3C] px-6 text-[15px] font-extrabold text-[#0B2160]"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!ready) return <DashboardSkeleton />;

  return (
    <CandidateShell page={tab === "account" ? "account" : "overview"} name={profile.name} email={profile.email}>
      <div className="mx-auto w-full max-w-[860px] px-6 pb-16 pt-8">
        <main>
          {tab === "overview" ? (
            <Overview profile={profile} showcase={showcase} />
          ) : tab === "account" ? (
            <AccountPanel profile={profile} onSaved={setProfile} />
          ) : (
            <FindMoreJobs profile={profile} />
          )}
        </main>
      </div>
    </CandidateShell>
  );
}

/**
 * Card data requested at the same time as the profile. Each card takes its
 * promise once, so coming back to Overview later fetches fresh data rather
 * than replaying a stale first load.
 */
let overviewPrefetch: { apps?: Promise<MyApplication[]>; jobs?: Promise<Job[]>; saved?: Promise<SavedJob[]> } = {};

function prefetchOverviewCards() {
  overviewPrefetch = { apps: fetchMyApplications(), jobs: fetchPublicJobs(), saved: fetchSavedJobs() };
  for (const p of Object.values(overviewPrefetch)) p.catch(() => undefined);
}

function takePrefetched<K extends keyof typeof overviewPrefetch>(key: K, load: () => NonNullable<(typeof overviewPrefetch)[K]>) {
  const p = overviewPrefetch[key] ?? load();
  delete overviewPrefetch[key];
  return p as NonNullable<(typeof overviewPrefetch)[K]>;
}

/** Placeholder rows shaped like the list items, shown while a card's own request is in flight. */
function ListSkeleton({ rows = 2 }: { rows?: number }) {
  return (
    <ul className="mt-3 border-t border-[#ECEAE3]" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <li key={i} className="flex items-center justify-between gap-3 border-b border-[#ECEAE3] py-3">
          <span className="h-4 w-[55%] animate-pulse rounded-[4px] bg-[#ECEAE3]" />
          <span className="h-[22px] w-24 animate-pulse rounded-full bg-[#ECEAE3]" />
        </li>
      ))}
    </ul>
  );
}

function Overview({ profile, showcase }: { profile: CandidateProfile; showcase: ShowcaseProfile }) {
  return (
    <div className="grid gap-4">
      <div className="mb-1">
        <h1 className="text-[32px] font-extrabold tracking-[-1px] text-[#0B2160]">
          {profile.name ? `Welcome back, ${profile.name.split(" ")[0]}.` : "Welcome back."}
        </h1>
        <p className="mt-1.5 text-[15px] text-[#5E6380]">Your profile, your applications and the jobs you saved.</p>
      </div>

      <ClosddProfileCard profile={profile} p={showcase} />

      <MyApplicationsCard />

      <SavedJobsCard />

      <HelpLine />
    </div>
  );
}

function HelpLine() {
  return (
    <p className="mt-2 text-[14px] text-[#5E6380]">
      Need help?{" "}
      <a href="mailto:info@closdd.com" className="font-bold text-[#0B2160] underline underline-offset-2">
        info@closdd.com
      </a>
    </p>
  );
}

const pill = "rounded-full px-2.5 py-[3px] text-[12px] font-bold";

/** "Your applications" list (design: Overview). */
function MyApplicationsCard() {
  const [apps, setApps] = useState<MyApplication[] | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);

  useEffect(() => {
    let off = false;
    takePrefetched("apps", fetchMyApplications)
      .then((a) => !off && setApps(a))
      .catch(() => !off && setApps([]));
    takePrefetched("jobs", fetchPublicJobs)
      .then((j) => !off && setJobs(j))
      .catch(() => {});
    return () => {
      off = true;
    };
  }, []);

  return (
    <section aria-labelledby="apps-h" className="rounded-lg border border-[#E3E1D9] bg-white px-6 py-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="apps-h" className="text-[17px] font-extrabold text-[#0B2160]">Your applications</h2>
        <Link to="/find-jobs" className="text-[15px] font-extrabold text-[#0B2160] underline underline-offset-2">Browse roles →</Link>
      </div>
      {apps === null ? (
        <ListSkeleton />
      ) : apps.length === 0 ? (
        <p className="mt-3 border-t border-[#ECEAE3] pt-3 text-[15px] text-[#4A4F6A]">
          You haven't applied for a role yet. Find one and apply with your pitch.
        </p>
      ) : (
        <ul className="mt-3 border-t border-[#ECEAE3]">
          {apps.map((a) => {
            const job = jobs.find((j) => String(j.id) === String(a.jobId));
            const status = (a.status || "").toLowerCase();
            const progressed = status && status !== "new" && status !== "applied";
            return (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-[#ECEAE3] py-3">
                <span>
                  <strong className="text-[#0B2160]">{job?.title ?? "Role"}</strong>{" "}
                  <span className="text-[#5E6380]">{job ? `· ${[companyName(job), job.location, job.setting].filter(Boolean).join(" · ")}` : ""}</span>
                </span>
                <span className="flex flex-wrap gap-1.5">
                  {a.createdByDistribution ? (
                    <span className={`${pill} bg-[#FFE8E1] text-[#C2381B]`}>Put forward by Closdd</span>
                  ) : (
                    <span className={`${pill} bg-[#E8ECF8] text-[#0B2160]`}>
                      Applied {a.appliedAt ? new Date(a.appliedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}
                    </span>
                  )}
                  {progressed ? (
                    <span
                      className={`${pill} ${
                        status.includes("short") ? "bg-[#0B2160] text-white" : status.includes("reject") ? "bg-[#FCE3DD] text-[#A62B12]" : "bg-[#DCE6FF] text-[#0B2160]"
                      }`}
                    >
                      {status.includes("reject") ? "Not a fit" : a.status}
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-3 text-[13px] text-[#5E6380]">
        Each role shows "Applied" or "Put forward by Closdd" until the employer listens to your pitch. Then it moves to Heard, Shortlisted, Not a fit, Interviewing or Hired. If an employer wants to talk, they contact you directly.
      </p>
    </section>
  );
}

/** "Saved jobs" list — roles saved with the Save button on a job page (design: Overview). */
function SavedJobsCard() {
  const [saved, setSaved] = useState<SavedJob[] | null>(null);

  useEffect(() => {
    let off = false;
    takePrefetched("saved", fetchSavedJobs)
      .then((s) => !off && setSaved(s))
      .catch(() => !off && setSaved([]));
    return () => {
      off = true;
    };
  }, []);

  const remove = (jobId: string) => {
    setSaved((s) => (s ?? []).filter((x) => x.jobId !== jobId));
    void unsaveJob(jobId).catch(() => undefined);
  };

  return (
    <section aria-labelledby="saved-h" className="rounded-lg border border-[#E3E1D9] bg-white px-6 py-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="saved-h" className="text-[17px] font-extrabold text-[#0B2160]">Saved jobs</h2>
        <span className="text-[14px] text-[#5E6380]">Roles you saved with the Save button on a job page</span>
      </div>
      {saved === null ? (
        <ListSkeleton />
      ) : saved.length === 0 ? (
        <p className="mt-3 border-t border-[#ECEAE3] pt-3 text-[15px] text-[#4A4F6A]">
          Nothing saved yet. Tap Save on any job page to keep it here.
        </p>
      ) : (
        <ul className="mt-3 border-t border-[#ECEAE3]">
          {saved.map((s) => (
            <li key={s.jobId} className="flex flex-wrap items-center justify-between gap-3 border-b border-[#ECEAE3] py-3">
              <span>
                <strong className="text-[#0B2160]">{s.title}</strong>{" "}
                <span className="text-[#5E6380]">· {[s.company, s.location, s.setting].filter(Boolean).join(" · ")}</span>
                {s.ref ? <span className="ml-1 font-mono text-[12px] text-[#5E6380]">REF {s.ref}</span> : null}
              </span>
              <span className="flex items-center gap-3.5">
                {s.open ? (
                  <a href={`/jobs/${(s.ref || s.jobId).toLowerCase()}`} className="text-[14px] font-extrabold text-[#C2381B] underline underline-offset-2">
                    Pitch for this role →
                  </a>
                ) : (
                  <span className="text-[14px] font-bold text-[#5E6380]">No longer open</span>
                )}
                <button type="button" onClick={() => remove(s.jobId)} className="text-[14px] font-bold text-[#4A4F6A] underline underline-offset-[3px]">
                  Remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** "My profile" card: status, progress, next step, share link and the three steps (design: Overview). */
function ClosddProfileCard({ profile, p }: { profile: CandidateProfile; p: ShowcaseProfile }) {
  const [copied, setCopied] = useState(false);

  const slug = p.slug || slugFor(profile.name || "salesperson", profile.email);
  const answered = assessmentDone(p);
  const pitchDone = Boolean(p.audioUrl) || profile.hasPitch;
  const steps = [
    { label: "Upload your CV", hash: "cv", done: Boolean(profile.cvName), note: profile.cvName ? "Done" : "Not uploaded" },
    { label: "Record your 60-second pitch", hash: "pitch", done: pitchDone, note: pitchDone ? "Done" : "Not recorded" },
    { label: "Answer the 5 assessment questions", hash: "assessment", done: answered >= 5, note: answered >= 5 ? "Done" : `${answered} of 5` },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const live = doneCount === 3 && Boolean(profile.approved);
  const waiting = doneCount === 3 && !live;
  const next = steps.find((s) => !s.done);
  const url = profileUrl(slug).replace(/^https?:\/\/(www\.)?/, "");

  const copy = () => {
    void navigator.clipboard?.writeText(profileUrl(slug));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
  };

  return (
    <section aria-labelledby="prof-h" className="overflow-hidden rounded-lg border border-[#E3E1D9] bg-white">
      <div className="px-6 py-[22px]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <h2 id="prof-h" className="text-[21px] font-extrabold text-[#0B2160]">My profile</h2>
            <span className={`${pill} ${live ? "bg-[#E2F3E9] text-[#0B6B40]" : "bg-[#FFF1C7] text-[#7A5200]"}`}>
              {live ? "Live" : waiting ? "Waiting for Closdd approval" : "Not live yet"}
            </span>
          </div>
          <span className="text-[14px] font-bold text-[#0B2160]">{doneCount} of 3 steps done</span>
        </div>
        <div role="progressbar" aria-valuenow={Math.round((doneCount / 3) * 100)} aria-valuemin={0} aria-valuemax={100} aria-label="Profile completion" className="mt-3 h-2 overflow-hidden rounded bg-[#ECEAE3]">
          <div className="h-2 bg-[#FF5A3C]" style={{ width: `${Math.round((doneCount / 3) * 100)}%` }} />
        </div>
        {live ? (
          <p className="mt-3 text-[14px] text-[#4A4F6A]">Your profile is live. Share your link with any employer.</p>
        ) : (
          <>
            <p className="mt-3 text-[14px] text-[#4A4F6A]">Your profile goes live once all 3 steps are done and Closdd has checked it.</p>
            <Link to="/my-profile" hash={next?.hash ?? ""} className="mt-3.5 inline-flex h-[42px] items-center rounded-[4px] bg-[#0B2160] px-[18px] text-[14px] font-extrabold text-white">
              Next step: {next ? next.label.charAt(0).toLowerCase() + next.label.slice(1) : "view your profile"} →
            </Link>
          </>
        )}
        <p className="mb-1.5 mt-4 text-[13px] font-bold text-[#2B3150]">
          Your profile link
          {!live ? <span className="font-medium text-[#7A5200]"> · employers can open it once your profile is live</span> : null}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex h-[42px] min-w-0 flex-[1_1_260px] items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-[4px] border border-[#E3E1D9] bg-[#FAF9F5] px-3 font-mono text-[13px] text-[#0B2160]">
            {url}
          </span>
          <Link to="/profile/$slug" params={{ slug }} target="_blank" rel="noopener" className="flex h-[42px] items-center rounded-[4px] border-[1.5px] border-[#0B2160] px-4 text-[14px] font-extrabold text-[#0B2160]">
            View
          </Link>
          <button type="button" onClick={copy} className="h-[42px] rounded-[4px] bg-[#0B2160] px-4 text-[14px] font-extrabold text-white">
            {copied ? "✓ Copied" : "Copy link"}
          </button>
        </div>
      </div>
      <ol aria-label="3 steps to go live">
        {steps.map((s, i) => (
          <li key={s.label} className="border-t border-[#ECEAE3]">
            <Link to="/my-profile" hash={s.hash} className="flex items-center gap-3.5 px-6 py-3.5">
              <span className={`grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full text-[13px] font-extrabold ${s.done ? "bg-[#E2F3E9] text-[#0B6B40]" : "bg-[#ECEAE3] text-[#0B2160]"}`}>
                {s.done ? "✓" : i + 1}
              </span>
              <span className="min-w-0 flex-1 text-[15px] font-bold text-[#0B2160]">{s.label}</span>
              <span className={`text-[13px] font-bold ${s.done ? "text-[#0B6B40]" : "text-[#5E6380]"}`}>{s.note}</span>
              <span aria-hidden className="text-[#7A7F99]">›</span>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Account: login and contact details plus password (design: CandAccount). CV and pitch live on My profile. */
function AccountPanel({ profile, onSaved }: { profile: CandidateProfile; onSaved: (p: CandidateProfile) => void }) {
  const [form, setForm] = useState({ name: profile.name, email: profile.email, phone: profile.phone });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({ name: profile.name, email: profile.email, phone: profile.phone });
  }, [profile]);

  const edit = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setSaved(false);
  };

  async function save() {
    setBusy(true);
    setError("");
    try {
      const next = await updateMyProfile({ ...profile, ...form });
      onSaved({ ...profile, ...next });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't save your changes.");
    } finally {
      setBusy(false);
    }
  }

  const fields: { k: keyof typeof form; label: string; type: string; ac: string }[] = [
    { k: "name", label: "Full name", type: "text", ac: "name" },
    { k: "email", label: "Email", type: "email", ac: "email" },
    { k: "phone", label: "Phone", type: "tel", ac: "tel" },
  ];

  return (
    <div className="mx-auto grid w-full max-w-[760px] gap-4">
      <div className="mb-1">
        <h1 className="text-[32px] font-extrabold tracking-[-1px] text-[#0B2160]">Account</h1>
        <p className="mt-1.5 text-[15px] text-[#5E6380]">
          Your login and contact details. Your CV, pitch and everything employers see live on{" "}
          <Link to="/my-profile" className="font-bold text-[#0B2160] underline underline-offset-2">My profile</Link>.
        </p>
      </div>

      <section aria-labelledby="det-h" className="rounded-lg border border-[#E3E1D9] bg-white p-[22px]">
        <h2 id="det-h" className="mb-[18px] text-[19px] font-extrabold text-[#0B2160]">Contact details</h2>
        <div className="grid items-end gap-4 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
          {fields.map((f) => (
            <label key={f.k} className="flex flex-col gap-1.5 text-[14px] font-bold text-[#2B3150]">
              {f.label}
              <input type={f.type} autoComplete={f.ac} value={form[f.k]} onChange={edit(f.k)} className={inputClass} />
            </label>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3.5">
          <button type="button" onClick={save} disabled={busy} className="h-11 rounded-[4px] bg-[#0B2160] px-5 text-[15px] font-extrabold text-white disabled:opacity-60">
            Save changes
          </button>
          {saved ? <span role="status" className="text-[14px] font-bold text-[#0B6B40]">✓ Changes saved</span> : null}
          {error ? <span role="alert" className="text-[14px] font-semibold text-[#A62B12]">{error}</span> : null}
        </div>
      </section>

      <PasswordSection />

      <HelpLine />
    </div>
  );
}

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function submit() {
    setError(null);
    setSaved(false);
    if (next.length < 8) {
      setError("Your new password needs at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("The new passwords don't match.");
      return;
    }
    try {
      await updatePasswordApi(current, next);
      setSaved(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't update your password.");
    }
  }

  return (
    <section aria-labelledby="pw-h" className="rounded-lg border border-[#E3E1D9] bg-white p-[22px]">
      <h2 id="pw-h" className="text-[19px] font-extrabold text-[#0B2160]">Password</h2>
      <p className="mb-[18px] mt-1 text-[14px] text-[#5E6380]">Change the password you use to log in.</p>
      <div className="flex max-w-[420px] flex-col gap-4">
        {(
          [
            ["Current password", current, setCurrent, "current-password"],
            ["New password", next, setNext, "new-password"],
            ["Confirm new password", confirm, setConfirm, "new-password"],
          ] as [string, string, (v: string) => void, string][]
        ).map(([label, value, set, ac]) => (
          <label key={label} className="flex flex-col gap-1.5 text-[14px] font-bold text-[#2B3150]">
            {label}
            <PasswordInput value={value} onChange={(e) => set(e.target.value)} className={`${inputClass} pr-12`} autoComplete={ac} />
          </label>
        ))}
        <p className="-mt-1.5 text-[13px] text-[#5E6380]">Use at least 8 characters.</p>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3.5">
        <button
          type="button"
          onClick={submit}
          className="h-11 rounded-[4px] border-[1.5px] border-[#0B2160] bg-white px-5 text-[15px] font-extrabold text-[#0B2160]"
        >
          Update password
        </button>
        {error ? <span className="text-[14px] font-semibold text-[#4A4F6A]">{error}</span> : null}
        {saved ? <span role="status" className="text-[14px] font-bold text-[#0B6B40]">✓ Password updated</span> : null}
      </div>
    </section>
  );
}
